class CartItemEdit {
  constructor() {
    this.modal = new MinimogTheme.Modal();
    this.isOpen = false;
    this.isLoading = false;
    this.currentEditData = null;
    this.modalContent = null;
    this.selectedVariant = null;
    this.onVariantPickerChange = this.onVariantPickerChange.bind(this);

    addEventDelegate({
      selector: '[data-cart-item-edit]',
      handler: (e, target) => {
        e.preventDefault();
        if (this.isLoading) return;
        this.openEditModal(target);
      },
    });

    MinimogEvents.subscribe(MinimogTheme.pubSubEvents.cartUpdate, () => {
      if (this.isOpen) this.close();
    });

    MinimogEvents.subscribe('ON_MODAL_CLOSED', () => {
      this.close();
    });
  }

  openEditModal(button) {
    const cartItem = button.closest('[data-cart-item]');

    const editData = {
      productHandle: button.dataset.productHandle,
      variantId: button.dataset.variantId,
      line: button.dataset.line,
      lineKey: button.dataset.lineKey,
      quantity: parseInt(button.dataset.quantity, 10) || 1,
      bundleGroupId: cartItem?.dataset.bundleGroupId || null,
      properties: this.getLineItemProperties(cartItem),
    };

    this.currentEditData = editData;
    this.isLoading = true;
    this.toggleLoading(button, true);

    const productUrl = `${window.MinimogSettings.base_url}products/${editData.productHandle}`;

    fetchSection('cart-item-edit', {
      url: productUrl,
      params: { variant: editData.variantId },
    })
      .then((html) => {
        const modalContent = html.querySelector('#CartItemEdit-content');
        if (!modalContent) throw new Error('Cart item edit section not found');

        this.modalContent = modalContent;
        const colorScheme = modalContent.dataset.colorScheme || '';

        this.modal.appendChild(modalContent);
        this.modal.setWidth('520px');
        this.executeScripts(modalContent);
        this.modal.open();
        this.modal.setSizes(`m-gradient ${colorScheme}`);

        this.bindVariantPicker(modalContent);
        this.syncVariantFromPicker(modalContent);
        this.preselectLineItemProperties(editData.properties);
        this.preselectProductAddOns(modalContent, editData.properties);
        this.bindModalActions(modalContent);

        this.isOpen = true;
      })
      .catch((error) => {
        console.error('Error loading cart item edit:', error);
        MinimogTheme.Notification.show({
          target: document.body,
          method: 'appendChild',
          type: 'error',
          message: window.MinimogStrings.cartError,
          last: 3000,
          sticky: false,
        });
      })
      .finally(() => {
        this.isLoading = false;
        this.toggleLoading(button, false);
      });
  }

  bindModalActions(modalContent) {
    const cancelButton = modalContent.querySelector('[data-cart-item-edit-cancel]');
    const saveButton = modalContent.querySelector('[data-cart-item-edit-save]');

    cancelButton?.addEventListener('click', () => this.close());
    saveButton?.addEventListener('click', () => this.saveChanges(modalContent));
  }

  bindVariantPicker(modalContent) {
    modalContent.removeEventListener('change', this.onVariantPickerChange);
    modalContent.addEventListener('change', this.onVariantPickerChange);
  }

  onVariantPickerChange(event) {
    if (!this.modalContent || !this.modalContent.contains(event.target)) return;
    if (event.target.closest('product-add-ons')) return;
    if (!event.target.closest('.m-cart-item-edit__options')) return;
    if (!event.target.matches('select, input[type="radio"]')) return;

    this.syncVariantFromPicker(this.modalContent);
  }

  syncVariantFromPicker(modalContent) {
    const selectedValues = this.getSelectedOptionValues(modalContent);
    const variant = this.findMatchingVariant(modalContent, selectedValues);

    if (variant) {
      this.applyVariant(modalContent, variant);
    }
  }

  getSelectedOptionValues(modalContent) {
    const values = [];

    modalContent.querySelectorAll('.m-cart-item-edit__options .m-product-option').forEach((optionBlock) => {
      const select = optionBlock.querySelector('select');
      if (select) {
        values.push(select.value);
        return;
      }

      const checked = optionBlock.querySelector('input[type="radio"]:checked');
      values.push(checked ? checked.value : null);
    });

    return values;
  }

  getProductVariants(modalContent) {
    const script = modalContent.querySelector('[data-product-variants]');
    if (!script) return [];

    try {
      const variants = JSON.parse(script.textContent);
      return Array.isArray(variants) ? variants : [];
    } catch (error) {
      console.warn('Unable to parse product variants', error);
      return [];
    }
  }

  findMatchingVariant(modalContent, selectedValues) {
    const variants = this.getProductVariants(modalContent);
    if (!variants.length) return null;

    return variants.find((variant) => {
      return selectedValues.every((value, index) => {
        if (value === null || value === undefined || value === '') return true;
        return variant.options[index] === this.normalizePropertyValue(value);
      });
    });
  }

  applyVariant(modalContent, variant) {
    this.selectedVariant = variant;

    const sectionId = modalContent.dataset.sectionId;
    const input = modalContent.querySelector(`#product-form-${sectionId} [name="id"]`);
    if (input) {
      input.value = variant.id;
    }

    const variantScript = modalContent.querySelector('variant-picker [data-selected-variant]');
    if (variantScript) {
      variantScript.textContent = JSON.stringify(variant);
    }

    this.updateOptionLabels(modalContent);
    this.updatePriceDisplay(modalContent, variant);

    MinimogEvents.emit(MinimogTheme.pubSubEvents.variantChange, {
      data: { variant, sectionId },
    });

    document.dispatchEvent(
      new CustomEvent('variant:changed', {
        detail: { variant, sectionId },
      })
    );
  }

  updateOptionLabels(modalContent) {
    modalContent.querySelectorAll('.m-cart-item-edit__options .m-product-option').forEach((optionBlock) => {
      const selectedLabel = optionBlock.querySelector('.option-label--selected');
      if (!selectedLabel) return;

      const select = optionBlock.querySelector('select');
      if (select) {
        selectedLabel.textContent = select.value;
        return;
      }

      const checked = optionBlock.querySelector('input[type="radio"]:checked');
      if (checked) {
        selectedLabel.textContent = checked.value;
      }
    });
  }

  shouldShowComparePrice(priceRoot) {
    const priceEl = priceRoot?.classList?.contains('m-price') ? priceRoot : priceRoot?.querySelector('.m-price');
    return priceEl?.dataset.comparePrice === 'true';
  }

  updatePriceDisplay(modalContent, variant) {
    if (!variant) return;

    const priceBlock = modalContent.querySelector('.m-cart-item-edit__price');
    if (!priceBlock) return;

    const priceEl = priceBlock.querySelector('.m-price');
    if (!priceEl) return;

    const showComparePrice = this.shouldShowComparePrice(priceEl);
    const regularPriceEl = priceEl.querySelector('.m-price__regular .m-price-item--regular');
    const salePriceEl = priceEl.querySelector('.m-price-item--sale');
    const comparePriceEl = priceEl.querySelector('.m-price__sale s');

    const formattedPrice = formatMoney(variant.price, window.MinimogSettings.money_format);
    const shouldShowSale = showComparePrice && variant.compare_at_price > variant.price;
    const formattedCompare = shouldShowSale
      ? formatMoney(variant.compare_at_price, window.MinimogSettings.money_format)
      : null;

    if (shouldShowSale && salePriceEl) {
      priceEl.classList.add('m-price--on-sale');
      if (comparePriceEl) comparePriceEl.textContent = formattedCompare;
      salePriceEl.textContent = formattedPrice;
    } else if (regularPriceEl) {
      priceEl.classList.remove('m-price--on-sale');
      regularPriceEl.textContent = formattedPrice;
    }
  }

  async saveChanges(modalContent) {
    const editData = this.currentEditData;
    if (!editData) return;

    const productAddOns = modalContent.querySelector('product-add-ons');
    if (productAddOns) {
      await this.saveBundleChanges(modalContent, editData, productAddOns);
      return;
    }

    this.syncVariantFromPicker(modalContent);

    const sectionId = modalContent.dataset.sectionId;
    const form = modalContent.querySelector(`#product-form-${sectionId}`);
    if (!form) return;

    const newVariantId = this.getSelectedVariantId(modalContent);
    if (!newVariantId) {
      MinimogTheme.Notification.show({
        target: modalContent.querySelector('.m-product-form-message') || modalContent,
        method: 'appendChild',
        type: 'warning',
        message: window.MinimogStrings.selectVariant,
        last: 3000,
        sticky: false,
      });
      return;
    }

    const newProperties = this.collectPropertiesFromModal(modalContent);
    const mergedProperties = this.mergeProperties(editData.properties, newProperties);

    const saveButton = modalContent.querySelector('[data-cart-item-edit-save]');
    saveButton?.classList.add('m-spinner-loading');

    try {
      let sectionsToBundle = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
      );

      const cartItems = document.querySelector('m-cart-items');
      cartItems?.loading?.start();

      let response;

      if (String(newVariantId) !== String(editData.variantId)) {
        const removeResponse = await this.changeCartLine({
          id: editData.lineKey,
          quantity: 0,
          sections: sectionsToBundle,
        });

        if (removeResponse.errors || removeResponse.status) {
          throw new Error(removeResponse.errors || removeResponse.description || removeResponse.message);
        }

        response = await this.addCartItem({
          id: Number(newVariantId),
          quantity: editData.quantity,
          properties: mergedProperties,
          sections: sectionsToBundle,
        });
      } else {
        response = await this.changeCartLine({
          id: editData.lineKey,
          quantity: editData.quantity,
          properties: mergedProperties,
          sections: sectionsToBundle,
        });
      }

      if (response.errors || response.status) {
        throw new Error(response.errors || response.description || response.message);
      }

      window.MinimogEvents.emit(MinimogTheme.pubSubEvents.cartUpdate, { cart: response });
      this.close();

      const notificationTarget =
        document.querySelector('m-cart-items') ||
        document.querySelector('m-cart-drawer') ||
        document.body;

      MinimogTheme.Notification.show({
        target: notificationTarget,
        method: cartItems ? 'prepend' : 'appendChild',
        type: 'success',
        message: window.MinimogStrings.cartItemUpdated,
        delay: cartItems ? 400 : 0,
        last: 3000,
        sticky: !cartItems,
      });
    } catch (error) {
      console.error('Error updating cart item:', error);
      MinimogTheme.Notification.show({
        target: modalContent.querySelector('.m-product-form-message') || modalContent,
        method: 'appendChild',
        type: 'error',
        message: error.message || window.MinimogStrings.cartError,
        last: 3000,
        sticky: false,
      });
    } finally {
      saveButton?.classList.remove('m-spinner-loading');
      document.querySelector('m-cart-items')?.loading?.finish();
    }
  }

  async saveBundleChanges(modalContent, editData, productAddOns) {
    this.syncVariantFromPicker(modalContent);
    productAddOns.syncState?.();

    if (!productAddOns.validate()) {
      MinimogTheme.Notification.show({
        target: modalContent.querySelector('.m-product-form-message') || modalContent,
        method: 'appendChild',
        type: 'warning',
        message: 'Please select all required options.',
        last: 3000,
        sticky: false,
      });
      return;
    }

    const newVariantId = this.getSelectedVariantId(modalContent) || productAddOns.getAddToCartVariantId();
    if (!newVariantId) {
      MinimogTheme.Notification.show({
        target: modalContent.querySelector('.m-product-form-message') || modalContent,
        method: 'appendChild',
        type: 'warning',
        message: window.MinimogStrings.selectVariant,
        last: 3000,
        sticky: false,
      });
      return;
    }

    const saveButton = modalContent.querySelector('[data-cart-item-edit-save]');
    saveButton?.classList.add('m-spinner-loading');

    try {
      let sectionsToBundle = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
      );

      const cartItems = document.querySelector('m-cart-items');
      cartItems?.loading?.start();

      const cart = await fetch(`${window.Shopify.routes.root}cart.js`).then((response) => response.json());
      const keysToRemove = this.getBundleKeysFromCart(cart, editData);
      const removeUpdates = {};

      keysToRemove.forEach((key) => {
        removeUpdates[key] = 0;
      });

      const removeResponse = await this.updateCart(removeUpdates, sectionsToBundle);
      if (removeResponse.status || removeResponse.errors) {
        throw new Error(removeResponse.description || removeResponse.errors || removeResponse.message);
      }

      const items = productAddOns.buildCartItems(Number(newVariantId), editData.quantity);
      const addResponse = await this.addCartItems(items, sectionsToBundle);

      if (addResponse.status || addResponse.errors) {
        throw new Error(addResponse.description || addResponse.errors || addResponse.message);
      }

      window.MinimogEvents.emit(MinimogTheme.pubSubEvents.cartUpdate, { cart: addResponse });
      this.close();

      const notificationTarget =
        document.querySelector('m-cart-items') ||
        document.querySelector('m-cart-drawer') ||
        document.body;

      MinimogTheme.Notification.show({
        target: notificationTarget,
        method: cartItems ? 'prepend' : 'appendChild',
        type: 'success',
        message: window.MinimogStrings.cartItemUpdated,
        delay: cartItems ? 400 : 0,
        last: 3000,
        sticky: !cartItems,
      });
    } catch (error) {
      console.error('Error updating bundle cart item:', error);
      MinimogTheme.Notification.show({
        target: modalContent.querySelector('.m-product-form-message') || modalContent,
        method: 'appendChild',
        type: 'error',
        message: error.message || window.MinimogStrings.cartError,
        last: 3000,
        sticky: false,
      });
    } finally {
      saveButton?.classList.remove('m-spinner-loading');
      document.querySelector('m-cart-items')?.loading?.finish();
    }
  }

  getBundleGroupId(properties = {}) {
    return (
      properties._bundle_group_id ||
      properties.bundle_group_id ||
      properties['_bundle_group_id'] ||
      properties['bundle_group_id'] ||
      null
    );
  }

  getBundleParentGroupId(properties = {}) {
    return (
      properties._bundle_parent_group_id ||
      properties.bundle_parent_group_id ||
      properties['_bundle_parent_group_id'] ||
      properties['bundle_parent_group_id'] ||
      null
    );
  }

  normalizeProperties(properties) {
    if (!properties) return {};

    if (Array.isArray(properties)) {
      return properties.reduce((acc, property) => {
        const name = property?.name || property?.first;
        const value = property?.value ?? property?.last;
        if (name) acc[name] = value;
        return acc;
      }, {});
    }

    return typeof properties === 'object' ? properties : {};
  }

  getBundleGroupIdFromDom(line) {
    const lineNumber = parseInt(line, 10);
    if (!Number.isFinite(lineNumber)) return null;

    const lineItem =
      document.getElementById(`MinimogCart-Item-${lineNumber}`) ||
      document.querySelector(`[data-cart-item][data-index="${lineNumber}"]`);

    return lineItem?.dataset?.bundleGroupId || null;
  }

  getBundleKeysFromDom(groupId) {
    const keys = new Set();
    if (!groupId) return keys;

    document.querySelectorAll('[data-cart-item][data-bundle-parent-group-id]').forEach((cartItemEl) => {
      if (String(cartItemEl.dataset.bundleParentGroupId || '') !== String(groupId)) return;

      const lineKey = cartItemEl.dataset.lineKey;
      if (lineKey) keys.add(lineKey);
    });

    return keys;
  }

  getBundleKeysFromCart(cart, editData) {
    const keys = new Set();
    const mainCartItem = cart.items.find((item) => item.key === editData.lineKey);

    if (mainCartItem?.key) {
      keys.add(mainCartItem.key);
    } else if (editData.lineKey) {
      keys.add(editData.lineKey);
    }

    const properties = this.normalizeProperties({
      ...this.normalizeProperties(editData.properties),
      ...this.normalizeProperties(mainCartItem?.properties),
    });

    const groupId =
      this.getBundleGroupId(properties) ||
      editData.bundleGroupId ||
      this.getBundleGroupIdFromDom(editData.line);

    if (!groupId) return keys;

    cart.items.forEach((item) => {
      if (keys.has(item.key)) return;

      const itemProperties = this.normalizeProperties(item.properties);
      if (String(this.getBundleParentGroupId(itemProperties) || '') === String(groupId)) {
        keys.add(item.key);
      }
    });

    this.getBundleKeysFromDom(groupId).forEach((key) => keys.add(key));

    return keys;
  }

  updateCart(updates, sections = []) {
    return fetch(`${MinimogSettings.routes.cart_update_url}`, {
      ...fetchConfig(),
      body: JSON.stringify({
        updates,
        sections,
        sections_url: window.location.pathname,
      }),
    }).then((response) => response.json());
  }

  addCartItems(items, sections = []) {
    return fetch(`${MinimogSettings.routes.cart_add_url}`, {
      ...fetchConfig(),
      body: JSON.stringify({
        items,
        sections,
        sections_url: window.location.pathname,
      }),
    }).then((response) => response.json());
  }

  preselectProductAddOns(modalContent, properties) {
    const productAddOns = modalContent.querySelector('product-add-ons');
    if (!productAddOns?.preselectFromProperties) return;

    productAddOns.preselectFromProperties(properties);
  }

  collectPropertiesFromModal(modalContent) {
    const properties = {};

    modalContent.querySelectorAll('[name^="properties["]').forEach((field) => {
      if (field.type === 'radio' && !field.checked) return;
      if (field.type === 'checkbox' && !field.checked) return;

      const match = field.name.match(/^properties\[(.+)\]$/);
      if (!match || !match[1] || /^\d+$/.test(match[1])) return;

      const value = this.decodeHtmlEntities(field.value).trim();
      if (value !== '') {
        properties[match[1]] = value;
      }
    });

    return properties;
  }

  mergeProperties(originalProperties, newProperties) {
    const merged = {};

    Object.entries(originalProperties || {}).forEach(([key, value]) => {
      if (/^\d+$/.test(key)) return;
      if (key.startsWith('_') || key.startsWith('__shopify')) {
        merged[key] = this.decodeHtmlEntities(String(value));
      }
    });

    Object.entries(newProperties).forEach(([key, value]) => {
      if (!key || /^\d+$/.test(key)) return;
      merged[key] = this.decodeHtmlEntities(String(value));
    });

    return merged;
  }

  getLineItemProperties(cartItem) {
    const propertiesScript = cartItem?.querySelector('[data-cart-item-properties]');
    if (!propertiesScript) return {};

    try {
      const parsed = JSON.parse(propertiesScript.textContent);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch (error) {
      console.warn('Unable to parse cart item properties', error);
      return {};
    }
  }

  decodeHtmlEntities(value) {
    if (typeof value !== 'string') return value;

    const textarea = document.createElement('textarea');
    textarea.innerHTML = value;
    return textarea.value;
  }

  normalizePropertyValue(value) {
    return this.decodeHtmlEntities(String(value || '')).trim();
  }

  getSelectedVariantId(modalContent) {
    if (this.selectedVariant?.id) {
      return String(this.selectedVariant.id);
    }

    const sectionId = modalContent.dataset.sectionId;
    const input = modalContent.querySelector(`#product-form-${sectionId} [name="id"]`);
    return input?.value ? String(input.value) : null;
  }

  changeCartLine(payload) {
    return fetch(`${MinimogSettings.routes.cart_change_url}`, {
      ...fetchConfig(),
      body: JSON.stringify(payload),
    }).then((response) => response.json());
  }

  addCartItem(payload) {
    return fetch(`${MinimogSettings.routes.cart_add_url}`, {
      ...fetchConfig(),
      body: JSON.stringify(payload),
    }).then((response) => response.json());
  }

  preselectLineItemProperties(properties) {
    if (!properties || !this.modalContent) return;
    if (this.modalContent.querySelector('product-add-ons')) return;

    const sizeSelector = this.modalContent.querySelector('size-selector');
    if (!sizeSelector) return;

    const optionName = sizeSelector.querySelector('[data-option-name]')?.dataset.optionName;
    if (!optionName) return;

    const selectedValue = this.normalizePropertyValue(properties[optionName]);
    if (!selectedValue) return;

    const inputs = sizeSelector.querySelectorAll('.m-product-option__input');
    inputs.forEach((input) => {
      if (this.normalizePropertyValue(input.value) === selectedValue) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  executeScripts(container) {
    container.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }

  toggleLoading(button, show) {
    button?.classList.toggle('m-spinner-loading', show);
  }

  close() {
    if (!this.isOpen) return;

    this.modalContent?.removeEventListener('change', this.onVariantPickerChange);

    this.modal?.close();
    this.isOpen = false;
    this.currentEditData = null;
    this.modalContent = null;
    this.selectedVariant = null;
  }
}

MinimogTheme.CartItemEdit = new CartItemEdit();
